import { bind, classNames, Component, h, render, signal, tag } from "omi";
import includes from "lodash-es/includes";
import tippy, { Instance, Props as TippyProps } from "tippy.js";
import tippyAnimations from "tippy.js/animations/scale-subtle.css?inline";

import { closeModal, NWIcon, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import type { DropdownItem } from "./models.ts";
import { NWDropdownMenu } from "./nw-dropdown-menu.tsx";

export * from "./models.ts";

type Props = {
  items: DropdownItem[];
  withBorder?: boolean;
  theme?: "transparent";
  width?: "fixed" | "fit" | "full";
  position?: "fixed" | "absolute";
  openByDefault?: boolean;
  openOrientation?: "top" | "bottom";
  customTrigger?: boolean;
  withAnimations?: boolean;
  limitHeight?: boolean;
  maxHeight?: string;
  withChevron?: boolean;
  fullWidthMenu?: boolean;
  isolateMouseEvents?: boolean;
  horizontalOrientation?: "left" | "right";
  autoposition?: boolean;
};

@tag("nw-dropdown")
export class NWDropdown extends Component<Props> {
  static css = [tailwind, tippyAnimations];
  static propTypes = {
    type: String,
    icon: String,
    iconStyle: String,
    text: String,
    subtext: String,
    theme: String,
    width: String,
    callback: Function,
    withTrigger: Boolean,
    withAnimations: Boolean,
    withBorder: Boolean,
    openByDefault: Boolean,
    openOrientation: String,
    limitHeight: Boolean,
    position: String,
    isolateMouseEvents: Boolean,
    horizontalOrientation: String,
    alignWithTrigger: Boolean,
    fullWidthMenu: Boolean,
    maxHeight: String,
    autoposition: Boolean,
  };

  private isOpen = signal<boolean>(false);
  private isClosing = signal<boolean>(false);
  private highlightedIndex = signal(-1);
  private tippyInstance: Instance<TippyProps> | undefined;

  install() {
    this.isOpen.value = this.props.openByDefault || false;
  }

  installed() {
    if (this.props.autoposition) {
      // Small delay to ensure DOM is ready
      setTimeout(() => this.initializeTippy(), 10);
    }
  }

  uninstall() {
    if (this.tippyInstance) {
      this.tippyInstance.destroy();
      this.tippyInstance = undefined;
    }
  }

  @bind
  private handleOpen(event: MouseEvent, shouldOpen: boolean): void {
    if (this.props.isolateMouseEvents) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.props.autoposition && this.tippyInstance) {
      // For Tippy.js, we need to check the actual visibility state
      const isCurrentlyVisible = this.tippyInstance.state.isVisible;

      if (shouldOpen && !isCurrentlyVisible) {
        this.tippyInstance.show();
      } else if (!shouldOpen || isCurrentlyVisible) {
        this.tippyInstance.hide();
      }
    } else {
      // Original behavior for non-autoposition dropdowns
      if (shouldOpen) {
        document.addEventListener("mousedown", this.withinBoundsListener);
        this.isClosing.value = false;
        this.isOpen.value = true;
      } else {
        document.removeEventListener("mousedown", this.withinBoundsListener);
        this.closeDropdown();
      }
    }
  }

  @bind
  private isDropdownOpen(): boolean {
    if (this.props.autoposition && this.tippyInstance) {
      return this.tippyInstance.state.isVisible;
    }
    return this.isOpen.value;
  }

  @bind
  private isDropdownOpenPeek(): boolean {
    if (this.props.autoposition && this.tippyInstance) {
      return this.tippyInstance.state.isVisible;
    }
    return this.isOpen.peek();
  }

  @bind
  private withinBoundsListener(event: MouseEvent): void {
    const path = event.composedPath();

    // Check if click is within the main component
    if (includes(path, this.rootElement)) {
      return;
    }

    // For Tippy dropdowns, also check if click is within the tippy popper
    if (this.props.autoposition && this.tippyInstance) {
      const tippyPopper = this.tippyInstance.popper;
      if (tippyPopper && includes(path, tippyPopper)) {
        return;
      }
      // Close Tippy dropdown
      this.tippyInstance.hide();
    } else {
      // Close regular dropdown
      this.closeDropdown();
    }
  }

  @bind
  private closeDropdown(): void {
    if (this.props.autoposition && this.tippyInstance) {
      this.tippyInstance.hide();
      return;
    }

    this.isClosing.value = true;

    onAnimationFinish(60, () => {
      this.isOpen.value = false;
    });
  }

  @bind
  private createDropdownElement(): HTMLElement {
    const container = document.createElement("div");
    
    // Apply full width styling to container when fullWidthMenu is enabled
    if (this.props.fullWidthMenu && this.props.width === "full") {
      container.style.width = "100%";
    }

    render(
      <NWDropdownMenu
        items={this.props.items}
        width={this.props.width || "fixed"}
        limitHeight={this.props.limitHeight !== false}
        maxHeight={this.props.maxHeight}
        fullWidthMenu={this.props.fullWidthMenu || false}
        isolateMouseEvents={this.props.isolateMouseEvents || false}
        highlightedIndex={this.highlightedIndex.value}
        onItemClick={this.handleItemClick}
        onMouseEnter={(idx: number) => (this.highlightedIndex.value = idx)}
        onMouseLeave={() => (this.highlightedIndex.value = -1)}
      />,
      container,
    );

    return container;
  }

  @bind
  private initializeTippy(): void {
    if (!this.props.autoposition) return;

    // Map orientation props to Tippy placement
    let placement: TippyProps["placement"];
    const { openOrientation = "bottom", horizontalOrientation = "left" } = this.props;

    if (openOrientation === "top") {
      placement = horizontalOrientation === "right" ? "top-end" : "top-start";
    } else {
      placement = horizontalOrientation === "right" ? "bottom-end" : "bottom-start";
    }

    // Find the trigger element
    const triggerElement = this.props.customTrigger
      ? this.rootElement?.querySelector("div")
      : this.rootElement?.querySelector("button");

    if (!triggerElement) {
      console.warn("NWDropdown: Trigger element not found for Tippy initialization");
      return;
    }

    const contentElement = this.createDropdownElement();

    const options: Partial<TippyProps> = {
      content: contentElement,
      placement,
      interactive: true,
      trigger: "manual",
      hideOnClick: false,
      animation: this.props.withAnimations !== false ? "scale-subtle" : false,
      duration: this.props.withAnimations !== false ? [150, 100] : [0, 0], // [show, hide]
      popperOptions: {
        strategy: this.props.position === "absolute" ? "absolute" : "fixed",
        modifiers: [
          {
            name: "flip",
            enabled: true,
          },
          {
            name: "preventOverflow",
            enabled: true,
          },
        ],
      },
      onShow: () => {
        // Update content when showing to ensure fresh data
        const newContent = this.createDropdownElement();
        this.tippyInstance?.setContent(newContent);
        // Add click-outside listener for Tippy dropdowns
        document.addEventListener("mousedown", this.withinBoundsListener);
      },
      onHide: () => {
        // Clean up event listeners when hiding
        document.removeEventListener("mousedown", this.withinBoundsListener);
      },
    };

    this.tippyInstance = tippy(triggerElement, options);
  }

  @bind
  private handleItemClick(item: DropdownItem, event?: MouseEvent): void {
    if (event && this.props.isolateMouseEvents) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.type != "divider" && item.isProOption && !Store.user.isPro()) {
      closeModal();
      setTimeout(() => {
        openProModal();
      }, 350);
      return;
    }

    if (item.type === "comingSoon") {
      // Coming soon options do not close the dropdown
      item.callback(item);
      return;
    }

    if (this.props.autoposition && this.tippyInstance) {
      this.tippyInstance.hide();
    } else {
      this.isOpen.value = false;
    }

    if (item.type === "button") {
      item.callback(item);
    }
  }

  @bind
  private handleKeyDown(event: KeyboardEvent) {
    if (!this.isOpen.value) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        this.isOpen.value = true;
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case "ArrowUp":
        this.highlightedIndex.value =
          this.highlightedIndex.value > 0 ? this.highlightedIndex.value - 1 : this.props.items.length - 1;
        event.preventDefault();
        break;
      case "ArrowDown":
        this.highlightedIndex.value =
          this.highlightedIndex.value < this.props.items.length - 1 ? this.highlightedIndex.value + 1 : 0;
        event.preventDefault();
        break;
      case "Enter":
      case " ":
        if (this.highlightedIndex.value !== -1) {
          this.handleItemClick(this.props.items[this.highlightedIndex.value]);
        }
        event.preventDefault();
        break;
      case "Escape":
        this.closeDropdown();
        event.preventDefault();
        break;
    }
  }

  render({
    withBorder = true,
    width = "fixed",
    openOrientation = "bottom",
    horizontalOrientation = "left",
    customTrigger = false,
    withAnimations = true,
    limitHeight = true,
    position = "fixed",
    withChevron = false,
    fullWidthMenu = false,
    maxHeight,
    autoposition = false,
  }: Props) {
    return (
      <div
        class={classNames("relative", { "transform-gpu": position === "fixed" && horizontalOrientation === "right" })}
      >
        {!customTrigger ? (
          <button
            id="trigger"
            class={classNames(
              "px-3 py-1.5 flex relative items-center rounded-lg transition-colors group hover:bg-gray-50 cursor-pointer border focus:shadow-xss",
              { "shadow-xss border-brand-300": this.isDropdownOpenPeek() },
              { "border-gray-200 bg-white": withBorder && !this.isDropdownOpenPeek() },
              {
                "border-transparent bg-transparent hover:border hover:border-gray-200":
                  !withBorder && !this.isDropdownOpenPeek(),
              },
              { "w-full": width === "full" },
            )}
            onClick={e => this.handleOpen(e, !this.isDropdownOpen())}
            onKeyDown={this.handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={this.isDropdownOpen()}
            aria-labelledby="trigger"
          >
            <div
              class={classNames("text-lg text-primary text-nowrap text-ellipsis overflow-hidden", {
                "flex items-center justify-between flex-1": withChevron,
              })}
            >
              <slot></slot>
              {withChevron && (
                <div className="bg-white h-[80%] flex items-center justify-center transition-colors group-hover:bg-gray-50 absolute right-[2px]">
                  <NWIcon
                    name="chevron-down"
                    class={classNames("w-3 h-3 mx-3 text-tertiary  transition-all", {
                      "rotate-180": this.isDropdownOpen(),
                    })}
                  />
                </div>
              )}
            </div>
          </button>
        ) : (
          <div onClick={e => this.handleOpen(e, !this.isDropdownOpen())}>
            <slot />
          </div>
        )}

        {!autoposition && this.isOpen.value && (
          <div
            class={classNames(
              { absolute: position === "absolute", fixed: position === "fixed" },
              "mt-1 z-[60]",
              withAnimations && {
                "animate-dropdownEntry": !this.isClosing.value && openOrientation === "bottom",
                "animate-dropdownExit": this.isClosing.value && openOrientation === "bottom",
                "animate-dropdownEntryUp": !this.isClosing.value && openOrientation === "top",
                "animate-dropdownExitUp": this.isClosing.value && openOrientation === "top",
              },
              {
                "bottom-11": openOrientation === "top" && customTrigger,
                "bottom-0": openOrientation === "top" && !customTrigger,
              },
              { "right-0": horizontalOrientation === "right" },
              { "w-full": fullWidthMenu && width === "full" },
            )}
          >
            <NWDropdownMenu
              items={this.props.items}
              width={width}
              limitHeight={limitHeight}
              maxHeight={maxHeight}
              fullWidthMenu={fullWidthMenu}
              isolateMouseEvents={this.props.isolateMouseEvents || false}
              highlightedIndex={this.highlightedIndex.value}
              onItemClick={this.handleItemClick}
              onMouseEnter={(idx: number) => (this.highlightedIndex.value = idx)}
              onMouseLeave={() => (this.highlightedIndex.value = -1)}
            />
          </div>
        )}
      </div>
    );
  }
}

function onAnimationFinish(animationDuration: number, callback: () => void): void {
  const start = performance.now();
  const animate = (time: number) => {
    if (time - start >= animationDuration) {
      callback();
    } else {
      requestAnimationFrame(animate);
    }
  };
  requestAnimationFrame(animate);
}
